/** Lib Imports */
import React from "react";
import PropTypes from "prop-types";
import { Button } from "primereact/button";
import { Checkbox } from "primereact/checkbox";

/** Components */
import Badges from "../badges/Badges";

/** Utilities */
import {
  formatNotificationDateTime,
  formatNotificationRelativeTime,
  getNotificationMessage,
  getNotificationPriorityDisplay,
  getNotificationReadStatusDisplay,
  getNotificationReferenceLabel,
  getNotificationTitle,
  getNotificationTypeDisplay,
  isNotificationUnread,
} from "../../../utils/common/notificationDisplay";

/** CSS */
import "./Notifications.scss";

const NotificationItem = ({
  notification,
  mode,
  onClick,
  onMarkRead,
  disabled,
  isActionLoading,
  showMarkReadAction,
  className,
  selectionMode,
  isSelected,
  onSelectionChange,
  isNotificationDropdown,
}) => {
  const isUnread = isNotificationUnread(notification);
  const typeDisplay = getNotificationTypeDisplay(notification?.type);
  const priorityDisplay = getNotificationPriorityDisplay(notification?.priority);
  const readStatusDisplay = getNotificationReadStatusDisplay(notification);
  const referenceLabel = getNotificationReferenceLabel(notification);
  const createdAtTooltip = formatNotificationDateTime(notification?.createdAt);
  const createdAtLabel =
    mode === "compact"
      ? formatNotificationRelativeTime(notification?.createdAt)
      : createdAtTooltip;

  const handleCheckboxChange = (event) => {
    if (!selectionMode || typeof onSelectionChange !== "function") {
      return;
    }

    onSelectionChange(notification, event.checked);
  };

  const handleClick = () => {
    if (disabled || isActionLoading || typeof onClick !== "function") {
      return;
    }

    onClick(notification);
  };

  const handleTitleClick = (event) => {
    if (!onClick || selectionMode) {
      return;
    }

    event.stopPropagation();
    handleClick();
  };

  const handleKeyDown = (event) => {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      handleClick();
    }
  };

  const isClickable = typeof onClick === "function" && !selectionMode;

  return (
    <div
      className={[
        "notification-item",
        `notification-item--${mode}`,
        isUnread ? "notification-item--unread" : "notification-item--read",
        isClickable ? "is-clickable" : "",
        disabled || isActionLoading ? "is-disabled" : "",
        selectionMode ? "notification-item--selectable" : "",
        className,
      ]
        .filter(Boolean)
        .join(" ")}
      onClick={isClickable ? handleClick : undefined}
      onKeyDown={isClickable ? handleKeyDown : undefined}
      role={isClickable ? "button" : undefined}
      tabIndex={isClickable ? 0 : undefined}
    >
      {selectionMode && (
        <div className="notification-item__selection">
          <Checkbox
            checked={Boolean(isSelected)}
            disabled={disabled || isActionLoading}
            onChange={handleCheckboxChange}
            aria-label={`Select notification ${notification?.title ?? ""}`}
          />
        </div>
      )}

      <div
        className={`notification-item__icon notification-item__icon--${typeDisplay.severity}`}
      >
        <i className={typeDisplay.icon}></i>
      </div>

      <div className="notification-item__content">
        <div className="notification-item__top-row">
          <div className="notification-item__title-wrap">
            {isUnread && <span className="notification-item__unread-dot"></span>}

            <h4 className="notification-item__title">
              {typeof onClick === "function" && !selectionMode ? (
                <button
                  type="button"
                  className="notification-item__title-button"
                  onClick={handleTitleClick}
                >
                  {getNotificationTitle(notification)}
                </button>
              ) : (
                getNotificationTitle(notification)
              )}
            </h4>
          </div>

          <div className="notification-item__meta">
            {mode === "full" && (
              <Badges
                label={readStatusDisplay.label}
                type={readStatusDisplay.badgeType}
                showDot={false}
              />
            )}
            {createdAtLabel && (
              <span className="notification-item__time" title={createdAtTooltip}>
                {createdAtLabel}
              </span>
            )}
          </div>
        </div>

        <p className="notification-item__message">
          {getNotificationMessage(notification)}
        </p>
            {!isNotificationDropdown && (
              <div className="notification-item__bottom-row">
                <div className="notification-item__chips">
                  <Badges
                    label={typeDisplay.label}
                    type={typeDisplay.badgeType}
                    showDot={false}
                  />
      
                  <Badges
                    label={priorityDisplay.label}
                    type={priorityDisplay.badgeType}
                    showDot={false}
                  />
      
                  {mode === "compact" && isUnread && !isNotificationDropdown && (
                    <span className="notification-item__state-text">Unread</span>
                  )}
                </div>
      
                {referenceLabel && (
                  <div className="notification-item__reference" title={referenceLabel}>
                    <i className="pi pi-link"></i>
                    <span>{referenceLabel}</span>
                  </div>
                )}
              </div>
            )}
      </div>

      {showMarkReadAction && isUnread && (
        <div className="notification-item__action">
          <Button
            icon={isActionLoading ? "pi pi-spin pi-spinner" : "pi pi-check"}
            className="p-button-text p-button-sm"
            tooltip="Mark as read"
            data-pr-position="bottom"
            disabled={disabled || isActionLoading}
            onClick={(event) => {
              event.stopPropagation();
              if (!disabled && !isActionLoading && typeof onMarkRead === "function") {
                onMarkRead(notification);
              }
            }}
          />
        </div>
      )}
    </div>
  );
};

NotificationItem.propTypes = {
  notification: PropTypes.shape({
    notificationId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    title: PropTypes.string,
    message: PropTypes.string,
    type: PropTypes.string,
    priority: PropTypes.string,
    referenceType: PropTypes.string,
    referenceId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    redirectUrl: PropTypes.string,
    isRead: PropTypes.bool,
    readAt: PropTypes.string,
    createdAt: PropTypes.string,
    sourceEventKey: PropTypes.string,
  }),
  mode: PropTypes.oneOf(["compact", "full"]),
  onClick: PropTypes.func,
  onMarkRead: PropTypes.func,
  disabled: PropTypes.bool,
  isActionLoading: PropTypes.bool,
  showMarkReadAction: PropTypes.bool,
  className: PropTypes.string,
  selectionMode: PropTypes.bool,
  isSelected: PropTypes.bool,
  onSelectionChange: PropTypes.func,
  isNotificationDropdown: PropTypes.bool,
};

export default NotificationItem;
